import { useEffect, useRef, useState } from "react";
import { Card, CardContent, Typography } from "@altimateai/lego";
import { TableInsightsSummary, TableStorageSummary } from "../../incidents";
import { CopyIconButton } from "../../../components/copyButton";
import { getIconByDatastoreType } from "../DatastoreImage";
import { Lineage } from "../../../components/lineage";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@altimateai/lego";
import {
  postColumns,
  altimateToAmundsen,
  postConnectedColumns,
  upstreamTables,
  downstreamTables,
  fetchDbtInfo,
} from "@/helpers/apis";
import { useQuery } from "@tanstack/react-query";
import { getTenant, isLineage } from "../../../helpers/tenant";
import { getFullyQualifiedName, getInstanceEdition, getScopeString } from "../../../helpers/utils";
import { TableAccessHistory } from "../TableAccessHistory";
import { Schema } from "../Schema";
import { BackButton } from "../../../components/button";
import { InsightSection } from "../../query/components";
import { Stack } from "@altimateai/lego";
import dayjs from "dayjs";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import TimeInfoDisclaimer from "../../../components/timeInfoDisclaimer";
import { getLastTableRefreshedTime } from "@/helpers/apis";
import Drawer, { DrawerRef } from "@/uiCore/components/drawer";
import TableAnalysisBot from "./tableAnalysisBot";
import { ViewsCodeSideModal } from "./ViewCodeSideModal";
import { getEntityFromScope } from "../utils";
import { TagsList } from "../../settings/tags/components/TagsList";
import { useAppState } from "@/modules/app/useAppContext";
import { Alert, AlertDescription } from "@altimateai/lego";
import { DatastoreInstanceEditionEnum } from "../../datastores/types";
import TableQueries from "@/pages/datasets/components/TableQueries";
import { DatatableContextProvider } from "@/components/tables/contexts/DatatableContextProvider";
import { IconInfoCircle } from "@tabler/icons-react";
import { DatasetTab, TableDetailResponse } from "../types";
import { Column, ViewsCodeModalArgs } from "@altimateai/ui-components/lineage";
import { GetOpportunitiesDataResponse } from "@/pages/explorer/types";
import { DatasetsTracking } from "@/lib/tracking/datasets";

const GET_LAST_REFRESHED_TIME = "GET-LAST-REFRESHED-TIME";

const DATASET_TAB_VALUES = Object.values(DatasetTab);

export const resolveTabFromParam = (tabParam: string | null): DatasetTab => {
  if (!tabParam) return DatasetTab.OPPORTUNITIES;

  if ((DATASET_TAB_VALUES as string[]).includes(tabParam)) {
    return tabParam as DatasetTab;
  }

  // Handle legacy numeric tab params — map index to tab name
  const tabIndex = parseInt(tabParam, 10);
  if (!isNaN(tabIndex)) {
    return DATASET_TAB_VALUES[tabIndex] ?? DatasetTab.OPPORTUNITIES;
  }

  return DatasetTab.OPPORTUNITIES;
};

interface MainContentProps {
  tableId: string | undefined;
  opportunities: GetOpportunitiesDataResponse[];
  table_rk: string | undefined;
  table: TableDetailResponse;
}

export const MainContent = ({ tableId, opportunities, table_rk, table }: MainContentProps) => {
  const { exportLineageEnabled, datastores } = useAppState();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const selectedOpportunityId = searchParams.get("opportunityId");
  const setSelectedOpportunityId = (v: string | undefined): void => {
    updateSearchParamsByKey({ opportunityId: v });
    if (v && tableId) {
      DatasetsTracking.opportunitySelected(tableId, v);
    }
  };
  const [viewCodeModalArgs, setViewCodeModalArgs] = useState<ViewsCodeModalArgs | null>(null);
  const drawerRef = useRef<DrawerRef>(null);
  const lineage = isLineage();
  const { startDate, endDate, onUpdate: onDateRangeChange } = useDateFromSearchParams();

  // Get selected tab from URL params; resolve legacy numeric params to tab names
  const selectedTab = resolveTabFromParam(searchParams.get("tab"));

  // Update URL when tab changes
  const handleTabChange = (value: string): void => {
    updateSearchParamsByKey({ tab: value });
    if (value === DatasetTab.SCHEMA && tableId) {
      DatasetsTracking.schemaViewed(tableId);
    } else if (value === DatasetTab.LINEAGE && tableId) {
      DatasetsTracking.lineageViewed(tableId);
    }
  };

  useEffect(() => {
    if (viewCodeModalArgs) {
      drawerRef.current?.open();
    } else {
      drawerRef.current?.close();
    }
  }, [viewCodeModalArgs]);

  const scope = getScopeString(table.scope);
  const {
    database: db_name,
    schema: schema_name,
    table: tableName,
    datastore,
  } = getEntityFromScope(table.scope);

  const renderTags = ({ column }: { column: Column }): JSX.Element => {
    if (column.tags?.length) console.log(column.tags, column.name);
    return (
      <TagsList
        tags={column.tags ?? []}
        entity={{
          datastore_type: table.datasource_type,
          table_rk: tableName,
          schema_rk: schema_name,
          datastore_rk: datastore,
          cluster_rk: db_name,
          column_rk: column.name,
        }}
      />
    );
  };

  const isApptioTenant = getTenant() === "apptio";
  const showSnowflakeNonEnterpriseInfo =
    getInstanceEdition(table.instance_id ?? 0, datastores || []) ===
    DatastoreInstanceEditionEnum.STANDARD;

  const full_table = `${db_name}.${schema_name}.${tableName}`;

  const { data: infoTimeString, isLoading } = useQuery({
    queryKey: [GET_LAST_REFRESHED_TIME],
    queryFn: () => getLastTableRefreshedTime(table_rk!),
    enabled: !!table_rk,
  });

  return (
    <Stack direction={"column"} className={"al-h-full al-w-full"}>
      <Stack direction="row" align="center" justify="between" className="al-w-full">
        <Stack direction="row" align="center">
          <BackButton defaultUrl="/datasets" />
          {getIconByDatastoreType(table.datasource_type, "al-size-8")}
          <Typography variant="subheading" className="text-primary fs-5 text-overflow">
            {tableName}
          </Typography>
        </Stack>
        <TableAnalysisBot tableName={full_table} instanceId={table.instance_id?.toString()} />
      </Stack>
      <Stack direction="row" justify="between" align="center">
        <Stack>
          <Stack direction="row" align="start">
            <Stack direction="column">
              <Stack direction="row" align="center" className="al-mt-2 al-mb-2">
                <div className="me-2 text-overflow">{scope}</div>
                <CopyIconButton value={getFullyQualifiedName(table.scope)} isLight />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        {!isLoading && infoTimeString?.last_refreshed && (
          <TimeInfoDisclaimer
            color="#ECEEEF"
            numOfSecondsActive={3}
            textToDisplay={
              <div className="text-muted al-me-2">
                The data was last updated at
                <br />
                <span className="text-black fw-semibold" aria-label="last refreshed time">
                  {dayjs
                    .utc(infoTimeString["last_refreshed"])
                    .local()
                    .format("D MMM, YYYY HH:mm:ss A")}
                </span>
              </div>
            }
          />
        )}
      </Stack>
      <div className="al-mb-2">
        <TagsList
          tags={table.tags ?? []}
          entity={{
            datastore_type: table.datasource_type,
            table_rk: tableName,
            schema_rk: schema_name,
            datastore_rk: datastore,
            cluster_rk: db_name,
          }}
        />
      </div>
      {selectedTab !== DatasetTab.LINEAGE && <TableStorageSummary tableId={tableId ?? ""} />}
      {(selectedTab === DatasetTab.OPPORTUNITIES || selectedOpportunityId) && (
        <TableInsightsSummary tableId={tableId ?? ""} />
      )}
      <Card className="al-grow al-mt-4" style={{ height: "93%" }}>
        <CardContent className="al-h-full al-pt-4 al-w-full">
          <Tabs value={selectedTab} onValueChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value={DatasetTab.SCHEMA}>Schema</TabsTrigger>
              <TabsTrigger value={DatasetTab.LINEAGE} disabled={!lineage}>
                Lineage
              </TabsTrigger>
              <TabsTrigger value={DatasetTab.OPPORTUNITIES}>Opportunities</TabsTrigger>
              <TabsTrigger value={DatasetTab.ACCESS_HISTORY}>Access History</TabsTrigger>
              <TabsTrigger value={DatasetTab.QUERIES}>Queries</TabsTrigger>
            </TabsList>

            <TabsContent value={DatasetTab.SCHEMA} className="al-flex-1 al-overflow-auto">
              {isApptioTenant ? (
                <Alert variant="destructive">
                  <AlertDescription>
                    <Stack direction="row" align="center" gap="sm">
                      <IconInfoCircle className="al-h-4 al-w-4" />
                      Schema is not available for this table.
                    </Stack>
                  </AlertDescription>
                </Alert>
              ) : (
                <Schema table={table} />
              )}
            </TabsContent>

            <TabsContent value={DatasetTab.LINEAGE} className="al-flex-1 al-overflow-auto">
              {showSnowflakeNonEnterpriseInfo ? (
                <Alert variant="info" className="al-bg-bg-brand">
                  <AlertDescription>
                    <Stack direction="row" align="center" gap="sm">
                      <IconInfoCircle className="al-h-4 al-w-4" />
                      Lineage is available only for Snowflake Enterprise Accounts (or higher).
                    </Stack>
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <Lineage
                    renderTags={renderTags}
                    tableId={tableId ?? ""}
                    getStartingNode={altimateToAmundsen}
                    postColumns={postColumns}
                    upstreamTables={upstreamTables}
                    downstreamTables={downstreamTables}
                    postConnectedColumns={postConnectedColumns}
                    dbtInfo={fetchDbtInfo}
                    setViewCodeModalArgs={setViewCodeModalArgs}
                    showViewCodeModal={false}
                    exportFinalLineage={exportLineageEnabled ?? false}
                  />
                  {viewCodeModalArgs && (
                    <Drawer ref={drawerRef} style={{ width: "600px" }}>
                      <ViewsCodeSideModal
                        viewsCodeArgs={{ ...viewCodeModalArgs, label: viewCodeModalArgs.table }}
                      />
                    </Drawer>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent
              value={DatasetTab.OPPORTUNITIES}
              className="al-flex-1 al-overflow-auto"
              aria-label="Opportunities tab content"
            >
              <InsightSection
                opportunities={opportunities}
                selectedOpportunityId={selectedOpportunityId}
                setOpportunityId={setSelectedOpportunityId}
                startDate={startDate}
                endDate={endDate}
                onDateRangeChange={onDateRangeChange}
                totalRuns=""
              />
            </TabsContent>

            <TabsContent value={DatasetTab.ACCESS_HISTORY} className="al-flex-1 al-overflow-auto">
              {isApptioTenant ? (
                <Alert variant="destructive">
                  <AlertDescription>
                    <Stack direction="row" align="center" gap="sm">
                      <IconInfoCircle className="al-h-4 al-w-4" />
                      Access history is not available for this table.
                    </Stack>
                  </AlertDescription>
                </Alert>
              ) : showSnowflakeNonEnterpriseInfo ? (
                <Alert variant="info" className="al-bg-bg-brand">
                  <AlertDescription>
                    <Stack direction="row" align="center" gap="sm">
                      <IconInfoCircle className="al-h-4 al-w-4" />
                      Access history is available only for Snowflake Enterprise Accounts (or
                      higher).
                    </Stack>
                  </AlertDescription>
                </Alert>
              ) : (
                <TableAccessHistory tableId={tableId ?? ""} />
              )}
            </TabsContent>

            <TabsContent value={DatasetTab.QUERIES} className="al-flex-1 al-overflow-auto">
              <DatatableContextProvider>
                <TableQueries tableId={tableId ?? ""} />
              </DatatableContextProvider>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </Stack>
  );
};
